import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Form,
  Select,
  Input,
  Alert,
  Spin,
  Tag,
  Space,
  message,
} from 'antd';
import { ClockCircleOutlined, LoginOutlined, LogoutOutlined } from '@ant-design/icons';
import { attendanceApi } from '../../utils/api';
import { handleApiError } from '../../utils/errorHandler';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { formatIranDate, formatIranTime, formatIranTimeFromIso } from '../../utils/iranTime';
import {
  applyOfficialClockOffset,
  formatDeviceTimeInTehran,
  formatSkewLabel,
  getDeviceClockSkewSeconds,
  getIranOfficialNow,
  resetOfficialClock,
  syncIranOfficialClockFromWorldTime,
} from '../../utils/iranOfficialClock';

const { Option } = Select;

const getGeoPosition = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('مرورگر از موقعیت مکانی پشتیبانی نمی‌کند'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: String(pos.coords.latitude),
          longitude: String(pos.coords.longitude),
        }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 },
    );
  });

const workFromLabels = {
  office: 'دفتر',
  home: 'دورکاری',
  other: 'سایر',
};

export default function ClockInOutModal({ open, onClose }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [todayData, setTodayData] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [clockTick, setClockTick] = useState(0);
  const [clockSynced, setClockSynced] = useState(false);

  const workFromType = Form.useWatch('work_from_type', form);

  useEffect(() => {
    if (!open) return undefined;
    resetOfficialClock();
    setClockSynced(false);
    syncIranOfficialClockFromWorldTime().then((ok) => {
      if (ok) setClockSynced(true);
    });
    const timer = setInterval(() => setClockTick((t) => t + 1), 1000);
    return () => clearInterval(timer);
  }, [open]);

  const officialNow = getIranOfficialNow();
  const formattedCurrentTime = formatIranTime(officialNow);
  const formattedCurrentDate = formatIranDate(officialNow);
  const deviceSkewSeconds = getDeviceClockSkewSeconds();
  const skewLabel = clockSynced ? formatSkewLabel(deviceSkewSeconds) : null;
  const formattedDeviceTime = formatDeviceTimeInTehran();
  void clockTick;
  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const [todayRes, modalRes] = await Promise.all([
        attendanceApi.getToday({
          onResponse: (response) => {
            const dateHeader = response.headers.get('Date');
            if (dateHeader && applyOfficialClockOffset(dateHeader, { source: 'server' })) {
              setClockSynced(true);
            }
          },
        }),
        attendanceApi.getClockInModal(),
      ]);
      setTodayData(todayRes?.data || todayRes || {});
      setModalData(modalRes?.data || modalRes || {});
    } catch (e) {
      handleApiError(e, 'خطا در دریافت وضعیت حضور');
      setTodayData(null);
      setModalData(null);
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ work_from_type: 'office' });
      load();
    }
  }, [open, load, form]);

  const locations = modalData?.location || todayData?.locations || [];
  const defaultLocation = locations.find((l) => l.is_default) || locations[0];
  const shift = todayData?.shift || modalData?.shiftAssigned;
  const current = todayData?.current_clock_in || todayData?.currentClockIn;
  const canClockIn = todayData?.can_clock_in;
  const canClockOut = todayData?.can_clock_out;
  const cannotLogin = modalData?.cannotLogin || todayData?.cannot_login || todayData?.cannotLogin;
  const isHoliday = todayData?.is_holiday;

  useEffect(() => {
    if (defaultLocation?.id && open) {
      form.setFieldValue('location', defaultLocation.id);
    }
  }, [defaultLocation, open, form]);

  const handleClockIn = async (values) => {
    setSubmitting(true);
    try {
      const geo = await getGeoPosition();
      const payload = {
        work_from_type: values.work_from_type,
        working_from: values.work_from_type === 'other' ? values.working_from || null : null,
        location: values.location,
        currentLatitude: geo.latitude,
        currentLongitude: geo.longitude,
      };
      const res = await attendanceApi.clockIn(payload);
      if (res?.status === 'fail') {
        throw new Error(res?.error?.message || 'ثبت ورود ناموفق بود');
      }
      message.success(res?.message || 'حضور با موفقیت ثبت شد');
      await load();
      onClose?.();
    } catch (e) {
      handleApiError(e, 'ثبت ورود ناموفق بود');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClockOut = async () => {
    if (!current?.id) return;
    setSubmitting(true);
    try {
      const geo = await getGeoPosition();
      const workType = form.getFieldValue('work_from_type') || current.working_from || 'office';
      const locationId = form.getFieldValue('location') || current.location_id;
      const payload = {
        currentLatitude: geo.latitude,
        currentLongitude: geo.longitude,
        clockOutLocation: String(locationId),
        clockOutWorkFromType: workType,
        clockOutWorkFrom: workType === 'other' ? form.getFieldValue('working_from') || null : null,
        id: String(current.id),
      };
      await attendanceApi.clockOut(current.id, payload);
      message.success('خروج با موفقیت ثبت شد');
      await load();
      onClose?.();
    } catch (e) {
      handleApiError(e, 'ثبت خروج ناموفق بود');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={
        <Space className="custom-font">
          <ClockCircleOutlined />
          ثبت ورود / خروج
        </Space>
      }
      onCancel={onClose}
      footer={null}
      width={480}
      destroyOnClose
      className="custom-font"
    >
      {loading ? (
        <div className="flex justify-center py-10">
          <Spin />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-center dark:border-blue-800 dark:bg-blue-900/20">
            <div className="text-xs text-gray-500 dark:text-gray-400">ساعت رسمی ایران</div>
            <div className="mt-1 text-2xl font-bold tabular-nums text-blue-700 dark:text-blue-300 dir-ltr">
              {formattedCurrentTime}
            </div>
            <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">{formattedCurrentDate}</div>
            {/* {skewLabel ? (
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] leading-relaxed text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                <div>
                  ساعت دستگاه شما:{' '}
                  <span className="font-semibold tabular-nums dir-ltr">{formattedDeviceTime}</span>
                </div>
                <div className="mt-0.5">
                  اختلاف با ساعت رسمی:{' '}
                  <span className="font-semibold">{skewLabel}</span>
                  {' — '}
                  لطفاً ساعت سیستم را اصلاح کنید.
                </div>
              </div>
            ) : clockSynced ? (
              <div className="mt-1.5 text-[10px] text-emerald-600 dark:text-emerald-400">
                ساعت دستگاه با ساعت رسمی هم‌خوان است
              </div>
            ) : (
              <div className="mt-1.5 text-[10px] text-gray-400">در حال همگام‌سازی ساعت رسمی…</div>
            )} */}
          </div>

          {isHoliday ? (            <Alert type="info" showIcon message="امروز تعطیل رسمی/شرکتی است." />
          ) : null}
          {cannotLogin ? (
            <Alert type="warning" showIcon message="در حال حاضر امکان ثبت حضور وجود ندارد." />
          ) : null}

          {shift ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-800/50">
              <div className="font-semibold">{shift.shift_name}</div>
              <div className="text-gray-500 text-xs mt-1">
                {shift.office_start_time} — {shift.office_end_time}
              </div>
            </div>
          ) : null}

          {current ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-900/20">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">ورود ثبت‌شده</span>
                <Tag color="green">{formatIranTimeFromIso(current.clock_in_time)}</Tag>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                محل: {workFromLabels[current.working_from] || current.working_from || '—'}
              </div>
            </div>
          ) : null}

          <Form form={form} layout="vertical" onFinish={handleClockIn}>
            <Form.Item name="work_from_type" label="محل کار" rules={[{ required: true }]}>
              <Select>
                <Option value="office">دفتر</Option>
                <Option value="home">دورکاری (منزل)</Option>
                <Option value="other">سایر</Option>
              </Select>
            </Form.Item>

            {workFromType === 'other' ? (
              <Form.Item name="working_from" label="توضیح محل کار">
                <Input placeholder="مثلاً ماموریت خارج از دفتر" />
              </Form.Item>
            ) : null}

            {locations.length ? (
              <Form.Item name="location" label="موقعیت/شعبه" rules={[{ required: true, message: 'موقعیت را انتخاب کنید' }]}>
                <Select placeholder="انتخاب موقعیت" showSearch optionFilterProp="children">
                  {locations.map((loc) => (
                    <Option key={loc.id} value={loc.id}>
                      {loc.location || loc.address}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            ) : null}

            <div className="flex flex-wrap gap-2 pt-2">
              {canClockIn && !cannotLogin ? (
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<LoginOutlined />}
                  loading={submitting}
                  className={PANEL_PRIMARY_BTN_CLASS}
                >
                  ثبت ورود
                </Button>
              ) : null}
              {canClockOut && current?.id ? (
                <Button
                  type="default"
                  danger
                  icon={<LogoutOutlined />}
                  loading={submitting}
                  onClick={handleClockOut}
                >
                  ثبت خروج
                </Button>
              ) : null}
              <Button onClick={onClose}>بستن</Button>
            </div>
          </Form>
        </div>
      )}
    </Modal>
  );
}
